import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { SoftphoneProvider } from "@/contexts/SoftphoneContext";
import { ConnectionProvider } from "@/contexts/ConnectionContext";

import Login from "@/pages/Login";
import SessionExpired from "@/pages/SessionExpired";
import Workspace from "@/pages/Workspace";
import Calls from "@/pages/Calls";
import Contacts from "@/pages/Contacts";
import Analytics from "@/pages/Analytics";
import StatusPage from "@/pages/StatusPage";
import Layout from "@/components/Layout";

// Admin
import AdminLayout from "@/admin/AdminLayout";
import AdminOverview from "@/admin/pages/Overview";
import AdminUsers from "@/admin/pages/Users";
import AdminGroups from "@/admin/pages/Groups";
import AgentUIConfig from "@/admin/pages/AgentUIConfig";
import CatalogsPage from "@/admin/pages/Catalogs";
import CrmScreensPage from "@/admin/pages/CrmScreens";
import IntegrationsVault from "@/admin/pages/IntegrationsVault";
import IntegrationsHubPage from "@/admin/pages/IntegrationsHub";
import BillingPage from "@/admin/pages/Billing";
import AuditLogPage from "@/admin/pages/AuditLog";
import TenantsPage from "@/admin/pages/Tenants";
import BrandingPage from "@/admin/pages/Branding";
import WorkingHoursPage from "@/admin/pages/WorkingHours";
import ChannelsHub from "@/admin/pages/ChannelsHub";
import ResourceCRUDPage from "@/admin/ResourceCRUDPage";
import AMDSettingsPage from "@/admin/pages/AMDSettings";
import LeadListsPage from "@/admin/pages/LeadLists";
import DNCListsPage from "@/admin/pages/DNCLists";
import DIDsPage from "@/admin/pages/DIDs";
import IVRBuilderPage from "@/admin/pages/IVRBuilder";
import TimeConditionsPage from "@/admin/pages/TimeConditions";
import ScorecardBuilderPage from "@/admin/pages/ScorecardBuilder";
import CampaignsPage from "@/admin/pages/Campaigns";
import VoiceBroadcastPage from "@/admin/pages/VoiceBroadcast";
import PredictiveDialerPage from "@/admin/pages/PredictiveDialer";
import ExtensionsRangePage from "@/admin/pages/ExtensionsRange";
import TelephonyPage from "@/admin/pages/Telephony";
import SIPTrunksPage from "@/admin/pages/SIPTrunksPage";
import AiSuitePage from "@/admin/pages/AiSuite";
import KnowledgeBase from "@/admin/pages/KnowledgeBase";
import Orchestration from "@/admin/pages/Orchestration";
import OrchestrationDashboard from "@/pages/OrchestrationDashboard";
import ReportsDashboardPage from "@/admin/pages/reports/ReportsDashboard";
import StandardReportsPage from "@/admin/pages/reports/StandardReports";
import CustomReportBuilderPage from "@/admin/pages/reports/CustomReportBuilder";
import ReportTemplatesPage from "@/admin/pages/reports/ReportTemplates";
import ScheduledReportsPage from "@/admin/pages/reports/ScheduledReports";
import LeaderboardsPage from "@/admin/pages/reports/Leaderboards";
import BroadcastReportPage from "@/admin/pages/reports/BroadcastReport";
import AgentPerformancePage from "@/admin/pages/reports/AgentPerformance";
import CallDetailReportPage from "@/admin/pages/reports/CallDetailReport";
import TrafficReportsPage from "@/admin/pages/reports/TrafficReports";
import OmniReportsPage from "@/admin/pages/reports/OmniReports";
import QualityConsolePage from "@/admin/pages/QualityConsole";
import ScreenRecordingPage from "@/admin/pages/ScreenRecording";
import WhatsAppCallingPage from "@/admin/pages/WhatsAppCalling";
import AccessControlPage from "@/admin/pages/AccessControl";
import SupervisorFloor from "@/pages/SupervisorFloor";
import { SLUG_TO_KIND } from "@/admin/slug-map";
import { PermissionsProvider } from "@/contexts/PermissionsContext";

const ADMIN_ROLES = ["admin", "super_admin", "super_super_admin"];

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="h-screen flex items-center justify-center bg-background"><div className="text-sm text-muted-foreground">Loading workspace…</div></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Slice 3.5g — Agent workspace is for FRONTLINE roles only.
  // Block super_super_admin / super_admin / admin from landing on /workspace.
  // Supervisors keep access because they handle overflow calls during peaks.
  const WORKSPACE_ROLES = new Set(["agent", "supervisor"]);
  if (!WORKSPACE_ROLES.has(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="h-screen flex items-center justify-center bg-background"><div className="text-sm text-muted-foreground">Loading admin console…</div></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/workspace" replace />;
  return children;
}

const SUPER_SUPER = "super_super_admin";
// Operator-only slug pages — never configurable via the access-control matrix.
const OPERATOR_SLUGS = new Set(["rate-plans"]);

function SuperSuperOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== SUPER_SUPER) return <Navigate to="/admin" replace />;
  return children;
}

// Wrapper for slug-based generic resource pages: /admin/queues, /admin/campaigns, etc.
// Access (hidden/view/edit/full) is enforced dynamically by AdminLayout + the API
// interceptor; only the fixed operator-only pages stay hard-gated here.
function ResourceBySlug() {
  const { slug } = useParams();
  const { user } = useAuth();
  const kind = SLUG_TO_KIND[slug];
  if (!kind) return <Navigate to="/admin" replace />;
  if (OPERATOR_SLUGS.has(slug) && user?.role !== SUPER_SUPER) return <Navigate to="/admin" replace />;
  return <ResourceCRUDPage kind={kind} />;
}

// Catalog wrapper: /admin/dispositions etc.
function CatalogBySlug({ slug }) {
  return <CatalogsPage kind={slug} />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TenantProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/session-expired" element={<SessionExpired />} />
              <Route path="/status" element={<StatusPage />} />

              {/* Admin route tree */}
              <Route path="/admin" element={<AdminProtected><PermissionsProvider><AdminLayout /></PermissionsProvider></AdminProtected>}>
                <Route index element={<AdminOverview />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                <Route path="live-dashboard" element={<SupervisorFloor />} />
                {/* Organization */}
                <Route path="users" element={<AdminUsers />} />
                <Route path="groups" element={<AdminGroups />} />
                <Route path="working-hours" element={<WorkingHoursPage />} />
                {/* Catalogs */}
                <Route path="dispositions" element={<CatalogBySlug slug="dispositions" />} />
                <Route path="pause-codes" element={<CatalogBySlug slug="pause-codes" />} />
                <Route path="wrapup-codes" element={<CatalogBySlug slug="wrapup-codes" />} />
                <Route path="crm-screens" element={<CrmScreensPage />} />
                {/* Special pages */}
                <Route path="agent-ui" element={<AgentUIConfig />} />
                <Route path="integrations" element={<IntegrationsVault />} />
                <Route path="integrations-hub" element={<IntegrationsHubPage />} />
                <Route path="branding" element={<BrandingPage />} />
                <Route path="channels" element={<ChannelsHub />} />
                <Route path="tenants" element={<SuperSuperOnly><TenantsPage /></SuperSuperOnly>} />
                <Route path="access-control" element={<SuperSuperOnly><AccessControlPage /></SuperSuperOnly>} />
                {/* Custom builders (replace JSON-based generic pages) */}
                <Route path="amd-settings" element={<AMDSettingsPage />} />
                <Route path="lead-lists" element={<LeadListsPage />} />
                <Route path="dnc-lists" element={<DNCListsPage />} />
                <Route path="dids" element={<DIDsPage />} />
                <Route path="ivr-flows" element={<IVRBuilderPage />} />
                <Route path="time-conditions" element={<TimeConditionsPage />} />
                <Route path="scorecards" element={<ScorecardBuilderPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="voice-broadcast" element={<VoiceBroadcastPage />} />
                <Route path="dialer" element={<PredictiveDialerPage />} />
                <Route path="extensions-range" element={<ExtensionsRangePage />} />
                <Route path="telephony" element={<TelephonyPage />} />
                <Route path="sip-trunks" element={<SIPTrunksPage />} />
                <Route path="ai-suite" element={<AiSuitePage />} />
                <Route path="kb" element={<KnowledgeBase />} />
                <Route path="orchestration" element={<Orchestration />} />
                <Route path="orchestration-live" element={<OrchestrationDashboard />} />
                {/* Slice 11 — Reports */}
                <Route path="reports/dashboard"    element={<ReportsDashboardPage />} />
                <Route path="reports/standard"     element={<StandardReportsPage />} />
                <Route path="reports/agent-performance" element={<AgentPerformancePage />} />
                <Route path="reports/call-detail"   element={<CallDetailReportPage />} />
                <Route path="reports/traffic"       element={<TrafficReportsPage />} />
                <Route path="reports/omnichannel"   element={<OmniReportsPage />} />
                <Route path="reports/custom"       element={<CustomReportBuilderPage />} />
                <Route path="report-templates"     element={<ReportTemplatesPage />} />
                <Route path="report-schedules"     element={<ScheduledReportsPage />} />
                <Route path="reports/leaderboards" element={<LeaderboardsPage />} />
                <Route path="reports/broadcast"    element={<BroadcastReportPage />} />
                <Route path="qm/console" element={<QualityConsolePage />} />
                <Route path="screen-recording" element={<ScreenRecordingPage />} />
                <Route path="wa-calling" element={<WhatsAppCallingPage />} />
                <Route path="billing" element={<BillingPage />} />
                {/* Generic resource pages — cover all slug-mapped kinds */}
                <Route path=":slug" element={<ResourceBySlug />} />
              </Route>

              {/* Workspace route tree (agents/supervisors) */}
              <Route path="/" element={<Protected><ConnectionProvider><SoftphoneProvider><Layout /></SoftphoneProvider></ConnectionProvider></Protected>}>
                <Route index element={<Navigate to="/workspace" replace />} />
                <Route path="workspace" element={<Workspace />} />
                <Route path="supervisor" element={<SupervisorFloor />} />
                <Route path="orchestration" element={<OrchestrationDashboard />} />
                <Route path="calls" element={<Navigate to="/calls/queue" replace />} />
                <Route path="calls/:tab" element={<Calls />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="analytics" element={<Analytics />} />
              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
